import React from 'react';
import { Helmet } from 'react-helmet';
import { Box, Text, ButtonLink, Heading, Spinner } from '@indeed/ifl-components';
import { Notification, Jobs, Check } from '@indeed/ifl-icons';
import PageTransition from '../../../components/PageTransition/PageTransition';
import { SuccessOrAgreement1 } from '@indeed/indeed-design-system-illustrations-spot';
import { SuccessHeader, SuccessHeaderSpot } from '../../../components/Illustrations/SuccessHeader';
import useGettext from '../../../util/GettextFactory';
import { jsProThemeButton } from '../components/jsProTheme/jsProTheme';
import { useInitialState } from '../../../lib/context/InitialStateContext';
import { useProctorGroups } from '@indeed/frontend-proctor-utils';
import { useIsProBrandingEnabled } from '../../../lib/hooks/useIsProBrandingEnabled';
import { useApplyForMeCopy } from '../hooks/useApplyForMeCopy';
import { useLocation } from 'react-router-dom';
import { useEventLogger } from '../../../lib/eventLogger/EventLoggerProvider';
import { useGetJsProPremiumAccessQuery } from './__generated__/CompletePage.generated';
import { useGetAutoApplyModeQuery } from '../automation-level/__generated__/AutomationLevelPage.generated';

/**
 * Complete Page for Auto Apply Setup
 * Success page shown after activating Auto Apply
 *
 * Figma Design: AFM setup complete page
 * https://www.figma.com/design/YqvUoqcp7hUVEjilAGM0ls/Auto-Apply-2026?node-id=19092-71640
 */
const CompletePage: React.FC = () => {
    const { homepageUrl } = useInitialState();
    const location = useLocation();
    const eventLogger = useEventLogger();
    const gt = useGettext();
    const { getGroup } = useProctorGroups();
    const { data, loading } = useGetJsProPremiumAccessQuery();
    const { data: modeData } = useGetAutoApplyModeQuery();
    const isProBrandingEnabled = useIsProBrandingEnabled();
    const copy = useApplyForMeCopy();
    const isReviewBeforeSubmit = modeData?.autoApplyInfo?.mode === 'REVIEW_BEFORE_SUBMIT';
    const showApplyForMeCompletionDesign = copy.isRebranding && !isProBrandingEnabled;

    const getAutoApplyLimitProBrandingText = (): string => {
        if (isReviewBeforeSubmit) {
            return gt.gettext(
                "We'll prepare up to 5 applications a day for you based on your preferences.",
            );
        }
        const proAccessTier =
            data?.jobSeekerProfile?.profile?.jsProPremiumAccess?.autoApplyAccess?.tier;
        if (proAccessTier === 'FREEMIUM' || proAccessTier === 'FREE_TRIAL') {
            const group = getGroup('indeed_pro_auto_apply_freemium_config_payload_tog');
            const payload = (group?.payload as { json: { quota: number; resetPeriod: string } })
                ?.json;
            if (payload && typeof payload.quota === 'number') {
                switch (payload.resetPeriod) {
                    case 'DAILY':
                        return gt.ngettext(
                            "We'll apply to up to {0} job a day for you based on your preferences.",
                            "We'll apply to up to {0} jobs a day for you based on your preferences.",
                            payload.quota,
                            [payload.quota],
                        );
                    case 'WEEKLY':
                        return gt.ngettext(
                            "We'll apply to up to {0} job a week for you based on your preferences.",
                            "We'll apply to up to {0} jobs a week for you based on your preferences.",
                            payload.quota,
                            [payload.quota],
                        );
                    case 'MONTHLY':
                        return gt.ngettext(
                            "We'll apply to up to {0} job a month for you based on your preferences.",
                            "We'll apply to up to {0} jobs a month for you based on your preferences.",
                            payload.quota,
                            [payload.quota],
                        );
                    default:
                        return gt.ngettext(
                            "We'll apply to up to {0} job for you based on your preferences.",
                            "We'll apply to up to {0} jobs for you based on your preferences.",
                            payload.quota,
                            [payload.quota],
                        );
                }
            }
        }
        return gt.gettext("We'll apply to up to 14 jobs a week for you based on your preferences.");
    };

    const getAutoApplyLimitNonProBrandingText = (): string => {
        if (isReviewBeforeSubmit) {
            return gt.gettext('Up to 5 applications prepared a day');
        }
        return gt.gettext('Up to 14 applications sent a week');
    };

    if (loading) {
        return (
            <PageTransition pageKey="auto-apply-complete">
                <Helmet>
                    <title>{copy.completeTitle}</title>
                    <meta name="description" content={copy.completeMetaDescription} />
                </Helmet>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minBlockSize: '200px',
                    }}
                >
                    <Spinner title={gt.gettext('Loading')} />
                </Box>
            </PageTransition>
        );
    }

    return (
        <PageTransition pageKey="auto-apply-complete">
            <Helmet>
                <title>{copy.completeTitle}</title>
                <meta name="description" content={copy.completeMetaDescription} />
            </Helmet>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                }}
            >
                {/* Completion heading and illustration */}
                {isProBrandingEnabled ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 3,
                            inlineSize: '100%',
                            maxInlineSize: '370px',
                            paddingInline: 4,
                        }}
                    >
                        <SuccessOrAgreement1
                            size="lg"
                            title="Application success"
                            aria-hidden
                            sx={{
                                inlineSize: '226px',
                                blockSize: '140px',
                                display: 'block',
                            }}
                        />
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                inlineSize: '100%',
                            }}
                        >
                            <Heading level={4} sx={{ marginBlockEnd: 0 }}>
                                {copy.productIsNowActive}
                            </Heading>
                            <Text
                                level={3}
                                variant="contrast"
                                sx={{ textAlign: 'center', fontWeight: 500 }}
                            >
                                {getAutoApplyLimitProBrandingText()}
                            </Text>
                        </Box>
                    </Box>
                ) : showApplyForMeCompletionDesign ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            inlineSize: '100%',
                            paddingInline: 4,
                        }}
                    >
                        <Heading
                            level={4}
                            sx={{
                                inlineSize: '100%',
                                marginBlockEnd: 0,
                                textAlign: 'center',
                            }}
                        >
                            {copy.productIsNowActive}
                        </Heading>
                        <SuccessHeaderSpot
                            sx={{
                                inlineSize: '10.125rem',
                                marginBlockStart: 6,
                            }}
                        />
                    </Box>
                ) : (
                    <SuccessHeader />
                )}

                {/* Padded content below the illustration */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: isProBrandingEnabled || showApplyForMeCompletionDesign ? 5 : 6,
                        inlineSize: '100%',
                        paddingInline: 4,
                        alignItems: showApplyForMeCompletionDesign ? 'center' : undefined,
                    }}
                >
                    {/* Success Section */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: isProBrandingEnabled ? 3 : 6,
                            inlineSize: showApplyForMeCompletionDesign ? '100%' : undefined,
                            maxInlineSize: showApplyForMeCompletionDesign
                                ? '22.3125rem'
                                : undefined,
                        }}
                    >
                        {/* The AFM Figma places the heading above the illustration. */}
                        {!isProBrandingEnabled && !showApplyForMeCompletionDesign && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    inlineSize: '100%',
                                    maxInlineSize: '370px',
                                }}
                            >
                                <Heading level={4} sx={{ marginBlockEnd: 0 }}>
                                    {copy.productIsNowActive}
                                </Heading>
                            </Box>
                        )}

                        {isProBrandingEnabled ? (
                            /* Info Cards */
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 5,
                                    inlineSize: '100%',
                                }}
                            >
                                {/* Notifications Card */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 2,
                                        alignItems: 'flex-start',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            flexShrink: 0,
                                            marginBlockStart: 's1',
                                        }}
                                    >
                                        <Notification size="sm" aria-hidden />
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 2,
                                            flex: 1,
                                        }}
                                    >
                                        <Text
                                            level={3}
                                            variant="contrast"
                                            sx={{
                                                fontWeight: 700,
                                                lineHeight: 'typography.body.large.lineHeight',
                                            }}
                                        >
                                            {gt.gettext('Get daily updates')}
                                        </Text>
                                        <Text
                                            level={2}
                                            sx={{
                                                lineHeight: 'typography.body.small.lineHeight',
                                            }}
                                        >
                                            {gt.gettext(
                                                "We'll send you daily emails to let you know what jobs we applied for you.",
                                            )}
                                        </Text>
                                    </Box>
                                </Box>

                                {/* My Jobs Card */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 2,
                                        alignItems: 'flex-start',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            flexShrink: 0,
                                            marginBlockStart: 's1',
                                        }}
                                    >
                                        <Jobs size="sm" aria-hidden />
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 2,
                                            flex: 1,
                                        }}
                                    >
                                        <Text
                                            level={3}
                                            variant="contrast"
                                            sx={{
                                                fontWeight: 700,
                                                lineHeight: 'typography.body.large.lineHeight',
                                            }}
                                        >
                                            {gt.gettext('Manage applications in one place')}
                                        </Text>
                                        <Text
                                            level={2}
                                            sx={{
                                                fontWeight: 'typography.body.small.fontWeight',
                                                lineHeight: 'typography.body.small.lineHeight',
                                            }}
                                        >
                                            {gt.split.gettext(
                                                'Manage your preference, resume, and job applications in {0}.',
                                                <Text as="span" sx={{ fontWeight: 700 }}>
                                                    {gt.gettext('My Jobs')}
                                                </Text>,
                                            )}
                                        </Text>
                                    </Box>
                                </Box>
                            </Box>
                        ) : (
                            /* Checkmark List */
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 3,
                                    inlineSize: '100%',
                                    ...(showApplyForMeCompletionDesign && {
                                        padding: 4,
                                        outline: '1px solid',
                                        outlineColor: 'border.weak',
                                        outlineOffset: '-1px',
                                        borderRadius: 'md',
                                        boxSizing: 'border-box',
                                        minBlockSize: '8rem',
                                    }),
                                }}
                            >
                                {[
                                    getAutoApplyLimitNonProBrandingText(),
                                    gt.gettext('Get notifications and email updates'),
                                    copy.manageProductInMyJobs,
                                ].map((item) => (
                                    <Box
                                        key={item}
                                        sx={{
                                            display: 'flex',
                                            gap: showApplyForMeCompletionDesign ? 3 : 2,
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Check
                                            size={showApplyForMeCompletionDesign ? 'md' : 'sm'}
                                            aria-hidden
                                            sx={{ flexShrink: 0 }}
                                        />
                                        <Text
                                            level={showApplyForMeCompletionDesign ? 2 : 3}
                                            variant={
                                                showApplyForMeCompletionDesign
                                                    ? 'contrast'
                                                    : undefined
                                            }
                                        >
                                            {item}
                                        </Text>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>

                    {/* Action Button */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            inlineSize: '100%',
                            maxInlineSize: showApplyForMeCompletionDesign ? '22.125rem' : undefined,
                        }}
                    >
                        <ButtonLink
                            href={homepageUrl}
                            variant={showApplyForMeCompletionDesign ? 'secondary' : 'primary'}
                            sx={
                                isProBrandingEnabled
                                    ? jsProThemeButton.primary
                                    : showApplyForMeCompletionDesign
                                      ? {
                                            borderColor: 'border.weak',
                                            borderRadius: 'md',
                                            color: 'foreground.action.primary',
                                        }
                                      : {}
                            }
                            full
                            onClick={() =>
                                eventLogger.logAutoApplySetupButtonClickEvent?.({
                                    currentPage: location.pathname,
                                    button: 'return-to-job-search',
                                })
                            }
                        >
                            {gt.gettext('Return to job search')}
                        </ButtonLink>
                    </Box>
                </Box>
            </Box>
        </PageTransition>
    );
};

export default CompletePage;
